/**
 * Multi Text Input
 * ====================
 * 
 * JS events fired on the [data-multi-image-input]:
 * 
 *  - mti-change (any change)
 *  - mti-delete
 *  - mti-add
 * 
 */

const safeParseJSON: Function = window["iw"].utils.safeParseJSON;

declare const jQuery: any;
const $ = jQuery;

export interface MtiCtrls
{
    getValues(): string[];
    getMaxNumValues(): number;
    deleteAllItems(): void;
}

export interface MtiCtrlsByName
{
    [inputName: string]: MtiCtrls;
}

export function initMultiTextInputs($wrap: any = undefined): MtiCtrlsByName
{
    if (!$wrap)
    {
        $wrap = $(document.body);
    }
    const $mtiWraps = $wrap.find(`[data-multi-text-input]`);
    const mtiCtrlsByName: MtiCtrlsByName = {};
    if ($mtiWraps.length === 0)
    {
        return mtiCtrlsByName;
    }
    $mtiWraps.each(function () {
        const $mtiWrap = $(this);
        const inputName: string = $mtiWrap.attr("data-multi-text-input");
        if (!inputName)
        {
            console.warn(`[data-multi-text-input] lacks name`);
            return;
        }
        mtiCtrlsByName[inputName] = initMultiTextInput($mtiWrap);
    });
    return mtiCtrlsByName;
}

// $mtiWrap - JQuery for [data-multi-text-input]
// This func takes care of the UI contained within the [data-multi-text-input].
// Returns object with methods updateValues(), etc..
export function initMultiTextInput($mtiWrap): MtiCtrls
{
    const $rowsWrap = $mtiWrap.find("[data-rows]");
    
    const maxNumValues: number = parseInt($mtiWrap.attr("data-max-values"), 10) || -1;
    const values: string[] = safeParseJSON($mtiWrap.attr("data-default-values") || "[]") || [];
    const placeholder: string = $mtiWrap.attr("data-placeholder") || "";

    const maxValuesReached = (): boolean => ((maxNumValues > 0) && (values.length >= maxNumValues));

    const updateWrapClass = (): void => {
        if (values.length === 0)
        {
            $mtiWrap.addClass("mti-empty");
        }
        else
        {
            $mtiWrap.removeClass("mti-empty");
        }
        if (maxValuesReached())
        {
            $mtiWrap.addClass("mti-max-reached");
        }
        else
        {
            $mtiWrap.removeClass("mti-max-reached");
        }
    };

    const renderRows = (): void => {
        $rowsWrap.empty();
        const numValues: number = values.length;
        for (let index = 0; index < numValues; ++index)
        {
            const value: string = values[index];
            const updateValue = updateValueAtIndex.bind(null, index);
            const deleteValue = deleteValueAtIndex.bind(null, index);
            $rowsWrap.append(valueToRow(value, updateValue, deleteValue, placeholder));
        }
    };

    const updateView = (): void => {
        updateWrapClass();
        renderRows();
        $mtiWrap.trigger("mti-view-updated");
    };

    const deleteValueAtIndex = (itemIndex: number): void => {
        if (values[itemIndex] === undefined)
        {
            throw new Error(`deleteItemAtIndex passed bad index ${itemIndex}`);
        }
        values.splice(itemIndex, 1);
        updateView();
        $mtiWrap.trigger("mti-delete").trigger("mti-change");
    };
    
    const updateValueAtIndex = (itemIndex: number, newValue: string): void => {
        if (values[itemIndex] === undefined)
        {
            throw new Error(`updateValueAtIndex passed bad index ${itemIndex}`);
        }
        values[itemIndex] = newValue;
        updateView();
        $mtiWrap.trigger("mti-change");
    };

    const deleteAllItems = (): void => {
        if (values.length === 0)
        {
            return;
        }
        values.length = 0;
        updateView();
        $mtiWrap.trigger("mti-delete").trigger("mti-change");
    };

    const addItem = (): void => {
        if (maxValuesReached())
        {
            return;
        }
        values.push("");
        updateView();
        $mtiWrap.trigger("mti-add").trigger("mti-change");
    };

    $mtiWrap.find("[data-add]").on("click", function (event) {
        addItem();
    });
    
    const init = () => {
        updateView();
        $mtiWrap.trigger("mti-init");
    };

    init();

    return {
        getValues(): string[] {
            return values;
        },

        deleteAllItems(): void {
            deleteAllItems();
        },

        getMaxNumValues(): number {
            return maxNumValues;
        }
    };
}

function valueToRow(value: string, updateValue: (newValue: string) => void, deleteItem: () => void, placeholder: string)
{
    const $input = $(`<input type="text" class="mti-input" />`).attr({ placeholder }).val(value).on("change", function (event) {
        updateValue($input.val());
    });

    const $inputWrap = $(`<div class="mti-input-wrap">`).append($input);

    const $delete = $(`<div class="delete-item-button">`).on("click", function (event) {
        deleteItem();
        return false;
    });

    const $buttonWrap = $(`<div class="mti-button-wrap">`).append($delete);

    const $row = $(`<div class="mti-input-row">`).append($inputWrap).append($buttonWrap);

    return $row;
}
